import * as React from 'react';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import { Badge, Tooltip } from '@mui/material';
import { ChangeEvent, useEffect } from 'react';
import { IoAddSharp, IoRemoveSharp } from 'react-icons/io5';

interface ICount {
    defaultCount: number;
    setNumberItems?: (
        key: number,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        nbPosts?: number,
        itemId?: number
    ) => void;
    id: number;
    itemId?: number;
    maxCount?: number;
    minCount?: number;
    // Shown in a tooltip when minus is clicked at the minimum
    minHint?: string;
}

export default function Counter({ defaultCount, setNumberItems, id, itemId, maxCount, minCount = 0, minHint }: ICount) {
    const [count, setCount] = React.useState(defaultCount ?? 0);
    const [showHint, setShowHint] = React.useState(false);
    const atMin = count <= minCount;
    const reduce = () => {
        if (atMin) {
            if (minHint) {
                setShowHint(true);
                setTimeout(() => setShowHint(false), 4000);
            }
            return;
        }
        setCount(prevState => prevState - 1);
    };
    const minusButton = (
        <Button
            // With a hint the button stays clickable, so the tooltip can explain why it does not go lower
            disabled={atMin && !minHint}
            aria-disabled={atMin}
            sx={atMin && minHint ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            aria-label="reduce"
            onClick={reduce}
        >
            <IoRemoveSharp />
        </Button>
    );

    useEffect(() => {
        if (setNumberItems) {
            setNumberItems(id, undefined, count, itemId);
        }
    }, [count]);

    useEffect(() => {
        if (defaultCount === 0) {
            setCount(0);
        }
    }, [defaultCount]);

    return (
        <Box
            sx={{
                color: 'action.active',
                display: 'flex',
                flexDirection: 'column',
                '& > *': {
                    marginBottom: 2,
                },
                '& .MuiBadge-root': {
                    marginRight: 4,
                },
            }}
        >
            <div>
                <ButtonGroup className="d-flex align-items-center">
                    {minHint ? (
                        <Tooltip
                            title={minHint}
                            open={showHint}
                            onClose={() => setShowHint(false)}
                            arrow
                            placement="top"
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                        >
                            {minusButton}
                        </Tooltip>
                    ) : (
                        minusButton
                    )}
                    <Badge className="mb-0" color="secondary" badgeContent={`${count}`} />
                    {!!maxCount && (
                        <>
                            <span className="mx-2">/</span>
                            <Badge className="mb-0" color="secondary" badgeContent={`${maxCount}`} />
                        </>
                    )}
                    <Button
                        disabled={!!maxCount && count >= maxCount}
                        aria-label="increase"
                        onClick={() => {
                            setCount(prevState => (prevState >= 0 ? prevState + 1 : prevState));
                        }}
                    >
                        <IoAddSharp />
                    </Button>
                </ButtonGroup>
            </div>
        </Box>
    );
}
